import React from "react";
import { ButtonRow } from "../../../designSystem/button/ButtonRow";
import { H2, H3 } from "../../../designSystem/typography/Heading";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { ChipAvatarContent, ChipConfig, InfoChip, ToggleChip } from "../../../designSystem/Chip";
import { SingleColumnLayout } from "../../_layouts/SingleColumnLayout";
import styled from "styled-components";


export const ChipOverview = (): JSX.Element =>
{
    const [selectedDefault, setSelectedDefault] = React.useState(true);
    const [notSelectedDefault, setNotSelectedDefault] = React.useState(false);

    const variants: ChipConfig[] = [
        { variant: undefined },
        { variant: 'filled-light' },
        { variant: 'filled' },
        { variant: 'filled-dark' },
        { variant: 'outlined' },
        { variant: 'outlined-blue' },
        { variant: 'warning' },
        { variant: 'error' },
        { variant: 'disabled' }
    ];

    const avatarVariants: ChipAvatarContent[] = [
        { text: 'FI', variant: 'filled' },
        { text: 'OU', variant: 'outlined' },
        { text: 'WA', variant: 'warning' },
        { text: 'DI', variant: 'dimmed' }
    ];

    const indexes: number[] = [];
    for (let i = 0; i < 20; i++)
    {
        indexes.push(i);
    }

    const coloringStrings = ['John', 'Jane', 'Alice', 'Bob', 'Charlie', 'David', 'Eve', 'Frank', 'Grace', 'Hannah', 'Ivy', 'Jack', 'Kathy', 'Liam', 'Mia', 'Noah', 'Olivia', 'Paul', 'Quinn', 'Rachel'];

    return (
        <SingleColumnLayout narrow>
            <>
                <Flex flexDirection="column" gap={16}>

                    <ButtonRow variant={"left-aligned"}>
                        <H2>Chip</H2>
                    </ButtonRow>

                    <H3>Height check</H3>
                    <BorderRow>
                        <Flex gap={8} flexWrap="wrap">
                            <InfoChip variant="filled">filled</InfoChip>
                            <InfoChip variant="filled" leadingIcon="add_circle_fill">filled+icon</InfoChip>
                            <InfoChip variant="filled" avatar={avatarVariants[1]}>filled+avatar</InfoChip>
                            <InfoChip variant="filled" leadingIcon="add_circle_fill" avatar={avatarVariants[1]}>filled+icon+avatar</InfoChip>

                            <ToggleChip selected={selectedDefault} onClick={() => setSelectedDefault(!selectedDefault)}>toggle-filled</ToggleChip>
                            <ToggleChip selected={selectedDefault} onClick={() => setSelectedDefault(!selectedDefault)} avatar={avatarVariants[1]}>toggle-filled+avatar</ToggleChip>
                            <ToggleChip selected={selectedDefault} onClick={() => setSelectedDefault(!selectedDefault)}>toggle-filled</ToggleChip>
                            <ToggleChip selected={selectedDefault} onClick={() => setSelectedDefault(!selectedDefault)} avatar={avatarVariants[1]}>toggle-filled+avatar</ToggleChip>
                        </Flex>
                    </BorderRow>

                    <H3>Infochip - variants</H3>
                    <BorderRow>
                        
                        <Flex gap={8} flexWrap="nowrap">
                            {variants.map((variant, index) => (
                                <InfoChip key={index} {...variant}>{variant?.variant || '[undefined]'}</InfoChip>
                            ))}
                        </Flex>

                        {variants.map((variant, index) => (
                            <Flex key={index} gap={8} flexWrap="wrap">
                                <InfoChip variant={variant.variant} leadingIcon="add_circle">{variant.variant || '[undefined]'} leading icon</InfoChip>
                                <InfoChip variant={variant.variant} trailingIcon="add_circle">{variant.variant || '[undefined]'} trailing icon</InfoChip>
                                <InfoChip variant={variant.variant} leadingIcon="add_circle" trailingIcon="add_circle">{variant.variant || '[undefined]'} leading and trailing icon</InfoChip>
                            </Flex>
                        ))}

                        <Flex gap={8} flexWrap="wrap">
                            <InfoChip variant="filled" avatar={{text:'AB'}}>avatar</InfoChip>
                            <InfoChip variant="filled" avatar={{text:'AB', variant:'dimmed'}}>avatar - dimmed</InfoChip>
                            <InfoChip variant="filled" avatar={{text:'AB', variant:'outlined'}}>avatar - outlined</InfoChip>
                            <InfoChip variant="filled" avatar={{text:'AB', variant:'warning'}}>avatar - warning</InfoChip>
                        </Flex>

                        <Flex gap={8} flexWrap="wrap">
                            <InfoChip variant="outlined" avatar={{text:'AB'}}>avatar</InfoChip>
                            <InfoChip variant="outlined" avatar={{text:'AB', variant:'dimmed'}}>avatar - dimmed</InfoChip>
                            <InfoChip variant="outlined" avatar={{text:'AB', variant:'outlined'}}>avatar - outlined</InfoChip>
                            <InfoChip variant="outlined" avatar={{text:'AB', variant:'warning'}}>avatar - warning</InfoChip>
                        </Flex>

                    </BorderRow>

                    <H3>InfoChip - color indexed</H3>
                    <BorderRow>
                        <Flex gap={8} flexWrap="wrap">
                            {indexes.map((index) => (
                                <InfoChip key={index} colorIndex={index}>Color {index}</InfoChip>
                            ))}
                        </Flex>
                    </BorderRow>

                    <H3>InfoChip - color hashed</H3>
                    <BorderRow>
                        <Flex gap={8} flexWrap="wrap">
                            {coloringStrings.map((str, index) => (
                                <InfoChip key={index} colorHash={str}>Color {str}</InfoChip>
                            ))}
                        </Flex>
                    </BorderRow>

                    <H3>ToggleChip - variants</H3>
                    <BorderRow>
                        {variants.map((variant, index) => (
                            <Flex key={index} gap={8} flexWrap="wrap">
                                <ToggleChip selectedStyle={variant} selected={selectedDefault} onClick={() => setSelectedDefault(!selectedDefault)}>Selected</ToggleChip>
                                <ToggleChip selectedStyle={variant} selected={selectedDefault} disabled>Selected disabled</ToggleChip>
                                <ToggleChip selectedStyle={variant} selected={notSelectedDefault} onClick={() => setNotSelectedDefault(!notSelectedDefault)}>Not selected</ToggleChip>
                                <ToggleChip selectedStyle={variant} selected={notSelectedDefault} disabled>Not selected disabled</ToggleChip>
                            </Flex>
                        ))}
                    </BorderRow>
                    <BorderRow>
                        <H3>ToggleChip - with avatars</H3>
                        {variants.map((variant, index1) => (
                            <Flex key={index1} gap={8} flexWrap="wrap">
                                {avatarVariants.map((avatar, index2) => (
                                    <div key={index2}>
                                        <ToggleChip selectedStyle={variant} key={index2} selected={selectedDefault} onClick={() => setSelectedDefault(!selectedDefault)} avatar={avatar}>Selected</ToggleChip>
                                        <ToggleChip selectedStyle={variant} key={index2} selected={selectedDefault} onClick={() => setSelectedDefault(!selectedDefault)} avatar={avatar}>Selected</ToggleChip>
                                    </div>
                                ))}
                            </Flex>
                        ))}
                    </BorderRow>
                    <H3>ToggleChip - color indexed</H3>
                    
                        <Flex gap={8} flexWrap="wrap">
                            {indexes.map((index) => (
                                <BorderRow key={index}>
                                    <ToggleChip selectedStyle={{colorIndex: index}} selected={selectedDefault}>Selected</ToggleChip>
                                    <ToggleChip selectedStyle={{colorIndex: index}} selected={selectedDefault} disabled>Selected disabled</ToggleChip>
                                    <ToggleChip selectedStyle={{colorIndex: index}} selected={notSelectedDefault}>Not selected</ToggleChip>
                                    <ToggleChip selectedStyle={{colorIndex: index}} selected={notSelectedDefault}>Not selected disabled</ToggleChip>
                                </BorderRow>
                            ))}
                        </Flex>
                </Flex>
            </>
        </SingleColumnLayout>
    );
};

export const BorderRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    border: 1px dotted black;
    padding: 10px;
`;